import type { PricingParameters } from "./types.js";
import { getDefaultParameters } from "./defaults.js";
import { generatePriceCurve } from "./pricing-engine.js";
import { runSimulation } from "./simulate.js";
import { createRng } from "./stats.js";
import { deepMerge, type DeepPartial } from "./merge.js";

/**
 * A representative synthetic route archetype. These are NOT real airline
 * fares or schedules — they are plausible, order-of-magnitude-correct
 * stand-ins (distance, fare, segment mix) spanning short-haul leisure,
 * business corridors, and long-haul international, used to demonstrate
 * that the engine behaves sensibly across a realistic operating envelope.
 */
export interface RouteArchetype {
  code: string;
  description: string;
  overrides: DeepPartial<PricingParameters>;
}

export const ROUTE_ARCHETYPES: RouteArchetype[] = [
  {
    code: "JFK-LAX",
    description: "Transcontinental business corridor",
    overrides: {
      flight: { capacity: 168, distanceMiles: 2475, baseFare: 380 },
      segments: [{ demandShare: 0.55 }, { demandShare: 0.45 }] as any,
      competitive: { competitorPriceIndex: 1.05 },
    },
  },
  {
    code: "MCO-BOS",
    description: "Vacation / leisure leisure-heavy route",
    overrides: {
      flight: { capacity: 180, distanceMiles: 1120, baseFare: 210 },
      segments: [{ demandShare: 0.85 }, { demandShare: 0.15 }] as any,
      competitive: { competitorPriceIndex: 0.95 },
    },
  },
  {
    code: "ORD-DEN",
    description: "Domestic business hub-to-hub",
    overrides: {
      flight: { capacity: 150, distanceMiles: 888, baseFare: 240 },
      competitive: { competitorPriceIndex: 1.0 },
    },
  },
  {
    code: "LAS-PHX",
    description: "Short-haul leisure",
    overrides: {
      flight: { capacity: 175, distanceMiles: 256, baseFare: 120 },
      segments: [{ demandShare: 0.9 }, { demandShare: 0.1 }] as any,
      competitive: { competitorPriceIndex: 0.9 },
    },
  },
  {
    code: "SEA-SFO",
    description: "West coast business shuttle",
    overrides: {
      flight: { capacity: 160, distanceMiles: 679, baseFare: 190 },
      segments: [{ demandShare: 0.55 }, { demandShare: 0.45 }] as any,
    },
  },
  {
    code: "MIA-JFK",
    description: "Mixed leisure/business coastal",
    overrides: {
      flight: { capacity: 190, distanceMiles: 1090, baseFare: 260 },
      segments: [{ demandShare: 0.7 }, { demandShare: 0.3 }] as any,
    },
  },
  {
    code: "ATL-LHR",
    description: "Long-haul international business",
    overrides: {
      flight: { capacity: 250, distanceMiles: 4200, baseFare: 950 },
      segments: [{ demandShare: 0.45 }, { demandShare: 0.55 }] as any,
      competitive: { competitorPriceIndex: 1.1 },
    },
  },
  {
    code: "DFW-ORD",
    description: "Domestic business hub-to-hub",
    overrides: {
      flight: { capacity: 175, distanceMiles: 802, baseFare: 220 },
    },
  },
  {
    code: "DEN-HNL",
    description: "Long-haul leisure",
    overrides: {
      flight: { capacity: 210, distanceMiles: 3200, baseFare: 520 },
      segments: [{ demandShare: 0.85 }, { demandShare: 0.15 }] as any,
      competitive: { competitorPriceIndex: 0.95 },
    },
  },
  {
    code: "PHX-SEA",
    description: "Balanced mixed-demand route",
    overrides: {
      flight: { capacity: 165, distanceMiles: 1107, baseFare: 230 },
      segments: [{ demandShare: 0.5 }, { demandShare: 0.5 }] as any,
    },
  },
];

export interface SyntheticFlightRecord {
  route: string;
  routeDescription: string;
  departureMonth: number;
  departureDayOfWeek: number;
  capacity: number;
  baseFare: number;
  competitorPriceIndex: number;
  dynamicRevenue: number;
  staticRevenue: number;
  revenueUpliftPct: number;
  dynamicLoadFactor: number;
  staticLoadFactor: number;
  flatPrice: number;
}

export interface ValidationDataset {
  generatedAt: string;
  flightsPerRoute: number;
  trialsPerFlight: number;
  records: SyntheticFlightRecord[];
  summary: {
    totalFlights: number;
    meanRevenueUpliftPct: number;
    medianRevenueUpliftPct: number;
    meanDynamicLoadFactor: number;
    meanStaticLoadFactor: number;
    upliftByRoute: { route: string; meanUpliftPct: number }[];
  };
}

/**
 * Builds a synthetic historical validation dataset: for each route
 * archetype, prices a spread of flight-dates across months/days-of-week
 * (with small randomized competitive-index noise) and runs the Monte
 * Carlo simulator to compare the dynamic policy against a revenue-
 * equivalent flat fare. This is the evidence exhibit referenced in
 * docs/technical-methodology.md — it is regenerated by
 * `pnpm generate:synthetic-data` and is fully reproducible from a fixed
 * seed.
 */
export function generateValidationDataset(
  options: { flightsPerRoute?: number; trialsPerFlight?: number; seed?: number } = {},
): ValidationDataset {
  const flightsPerRoute = options.flightsPerRoute ?? 24;
  const trialsPerFlight = options.trialsPerFlight ?? 150;
  const rng = createRng(options.seed ?? 20240115);

  const records: SyntheticFlightRecord[] = [];

  for (const route of ROUTE_ARCHETYPES) {
    const baseParams = deepMerge(getDefaultParameters(), route.overrides);

    for (let f = 0; f < flightsPerRoute; f++) {
      const month = 1 + Math.floor(rng() * 12);
      const dow = Math.floor(rng() * 7);
      const competitorNoise = 0.9 + rng() * 0.25;

      const params: PricingParameters = deepMerge(baseParams, {
        flight: { departureMonth: month, departureDayOfWeek: dow },
        competitive: { competitorPriceIndex: baseParams.competitive.competitorPriceIndex * competitorNoise },
      });

      const priceCurve = generatePriceCurve(params);
      const sim = runSimulation(params, { trials: trialsPerFlight, seed: Math.floor(rng() * 1e9) });

      records.push({
        route: route.code,
        routeDescription: route.description,
        departureMonth: month,
        departureDayOfWeek: dow,
        capacity: params.flight.capacity,
        baseFare: params.flight.baseFare,
        competitorPriceIndex: params.competitive.competitorPriceIndex,
        dynamicRevenue: sim.dynamic.meanRevenue,
        staticRevenue: sim.staticBaseline.meanRevenue,
        revenueUpliftPct: sim.revenueUpliftPct,
        dynamicLoadFactor: sim.dynamic.meanLoadFactor,
        staticLoadFactor: sim.staticBaseline.meanLoadFactor,
        flatPrice: sim.staticBaseline.flatPrice,
      });

      // priceCurve is computed for potential future per-flight curve export; referenced to avoid unused warnings.
      void priceCurve;
    }
  }

  const upliftValues = records.map((r) => r.revenueUpliftPct).sort((a, b) => a - b);
  const median = upliftValues.length % 2 === 0
    ? (upliftValues[upliftValues.length / 2 - 1]! + upliftValues[upliftValues.length / 2]!) / 2
    : upliftValues[Math.floor(upliftValues.length / 2)]!;

  const upliftByRoute = ROUTE_ARCHETYPES.map((route) => {
    const routeRecords = records.filter((r) => r.route === route.code);
    const meanUplift = routeRecords.reduce((a, r) => a + r.revenueUpliftPct, 0) / routeRecords.length;
    return { route: route.code, meanUpliftPct: meanUplift };
  });

  return {
    generatedAt: new Date().toISOString(),
    flightsPerRoute,
    trialsPerFlight,
    records,
    summary: {
      totalFlights: records.length,
      meanRevenueUpliftPct: records.reduce((a, r) => a + r.revenueUpliftPct, 0) / records.length,
      medianRevenueUpliftPct: median,
      meanDynamicLoadFactor: records.reduce((a, r) => a + r.dynamicLoadFactor, 0) / records.length,
      meanStaticLoadFactor: records.reduce((a, r) => a + r.staticLoadFactor, 0) / records.length,
      upliftByRoute,
    },
  };
}
